import React, { useState, useEffect } from 'react';

/**
 * TimePicker
 * Mobile-friendly 2-dropdown time picker (Hour 00-23 + Minute 00/15/30/45 only).
 * Drop-in replacement for <input type="time" ... />.
 *
 * Props:
 *  - value: string "HH:MM" (or '')
 *  - onChange: (newValue: string) => void   // emits "HH:MM"
 *  - disabled: bool
 *  - required: bool
 *  - className: applied to wrapper (pass "w-full" to stretch)
 *  - data-testid: base test id (applies "{id}-hour" / "{id}-minute" to selects)
 *
 * Internal: keeps its own hour/minute state so picks survive even when the
 * parent doesn't echo the value back via the `value` prop.
 */
const MINUTE_OPTIONS = ['00', '15', '30', '45'];
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) =>
  i.toString().padStart(2, '0')
);

function snapMinute(rawMinute) {
  if (rawMinute === undefined || rawMinute === null || rawMinute === '') return '';
  const m = parseInt(rawMinute, 10);
  if (Number.isNaN(m)) return '';
  const padded = m.toString().padStart(2, '0');
  if (MINUTE_OPTIONS.includes(padded)) return padded;
  // Snap to nearest valid quarter-hour for nicer UX
  const snapped = Math.round(m / 15) * 15;
  const clamped = snapped >= 60 ? 45 : snapped;
  return clamped.toString().padStart(2, '0');
}

function parseValue(value) {
  const [rawH = '', rawM = ''] = (value || '').split(':');
  const h = HOUR_OPTIONS.includes(rawH) ? rawH : '';
  const m = snapMinute(rawM);
  return { h, m };
}

export function TimePicker({
  value = '',
  onChange,
  disabled = false,
  required = false,
  className = '',
  'data-testid': testId,
}) {
  const initial = parseValue(value);
  // Local state so user picks render immediately, even when parent
  // doesn't echo the value back (e.g. uncontrolled usages).
  const [hour, setHour] = useState(initial.h);
  const [minute, setMinute] = useState(initial.m);

  // Sync from parent if `value` prop changes externally.
  useEffect(() => {
    const parsed = parseValue(value);
    setHour(parsed.h);
    setMinute(parsed.m);
  }, [value]);

  const emit = (h, m) => {
    if (!onChange) return;
    if (!h && !m) {
      onChange('');
      return;
    }
    onChange(`${h || '00'}:${m || '00'}`);
  };

  const handleHourChange = (newHour) => {
    const finalMinute = minute || '00';
    setHour(newHour);
    setMinute(finalMinute);
    emit(newHour, finalMinute);
  };

  const handleMinuteChange = (newMinute) => {
    const finalHour = hour || '00';
    setHour(finalHour);
    setMinute(newMinute);
    emit(finalHour, newMinute);
  };

  const baseSelect =
    'flex-1 min-w-[54px] px-1.5 py-2 border border-gray-300 rounded-lg text-sm bg-white ' +
    'focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed';

  return (
    <div
      className={`inline-flex items-center gap-1 ${className}`}
      data-testid={testId}
    >
      <select
        value={hour}
        onChange={(e) => handleHourChange(e.target.value)}
        disabled={disabled}
        required={required}
        className={baseSelect}
        data-testid={testId ? `${testId}-hour` : undefined}
        aria-label="Jam"
      >
        <option value="" disabled>
          --
        </option>
        {HOUR_OPTIONS.map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </select>
      <span className="text-gray-500 font-semibold select-none">:</span>
      <select
        value={minute}
        onChange={(e) => handleMinuteChange(e.target.value)}
        disabled={disabled}
        required={required}
        className={baseSelect}
        data-testid={testId ? `${testId}-minute` : undefined}
        aria-label="Menit"
      >
        <option value="" disabled>
          --
        </option>
        {MINUTE_OPTIONS.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>
    </div>
  );
}

export default TimePicker;
